import { useCallback, useEffect, useState } from 'react';

import { defaultConfig as defaultDurationConfig } from 'component/Search/Duration/config';

import { useSetAtom } from 'jotai';

import {
    useDepartureDate,
    useReturnDate,
    useTravelDuration,
} from 'hooks/form/search';

import { crossValidationErrorAtom } from 'data/Atom/Component';

import DateUtils from 'utility/Format/Date/DateUtils';

import { DurationType, DurationValue } from 'types/Search/form';

export enum CrossValidationError {
    // —> returnDate > 56 days —> open durationLayer, error
    MaxDurationExceeded = 'MaxDurationExceeded',
    // - first set short dates (2 days diff) and then set duration e.g. to 1 week -> set returnDate respective duration and open returnDatepicker
    // - first change duration and then set shorter dates -> open durationLayer, error
    DurationLonger = 'DurationLonger',
    DurationLongerFromDatepicker = 'DurationLongerFromDatepicker',
    NoError = 'NoError',
}

export enum ValidationType {
    Datepicker,
    Duration,
}

const getDurationInDays = (
    durationValue: DurationValue,
    from?: Date,
    to?: Date
): number[] => {
    switch (durationValue.type) {
        case DurationType.Week:
            return [durationValue.amount * 7];
        case DurationType.Day:
            return [durationValue.amount];
        case DurationType.Range:
            return [durationValue.min, durationValue.max];
        case DurationType.Exact:
            if (from && to) {
                return [DateUtils.getDateDiff(to, from)];
            }
    }

    return [];
};

export const useCrossDateValidation = () => {
    const [departureDate] = useDepartureDate();
    const [returnDate, setReturnDate] = useReturnDate();
    const [hasValidationChange, setHasValidationChange] = useState(false);
    const [durationValue] = useTravelDuration();
    const setError = useSetAtom(crossValidationErrorAtom);

    const validate = useCallback(
        (
            from: Date,
            to: Date,
            duration: DurationValue,
            type: ValidationType
        ) => {
            const [minDuration, maxDuration] = getDurationInDays(
                duration,
                from,
                to
            );
            const durationLongerError =
                type === ValidationType.Datepicker
                    ? CrossValidationError.DurationLongerFromDatepicker
                    : CrossValidationError.DurationLonger;
            const selectedDays = DateUtils.getDaysDiff(to, from);
            // durationType !== DurationType.Range
            if (minDuration && !maxDuration) {
                if (minDuration > defaultDurationConfig.maxDays) {
                    return {
                        error: CrossValidationError.MaxDurationExceeded,
                        offset: 0,
                    };
                }

                // durationCount is for difference between day and week validation
                const durationCount =
                    duration.type === DurationType.Week
                        ? minDuration
                        : minDuration - 1;
                const futureDate = DateUtils.addDays(from, durationCount);
                const daysDiff = DateUtils.getDaysDiff(futureDate, from);
                if (Math.ceil(selectedDays) < daysDiff) {
                    return {
                        error: durationLongerError,
                        offset: daysDiff - 1,
                    };
                }
            }
            // durationType === DurationType.Range
            if (minDuration && maxDuration) {
                // check if selected dates are within the range
                const listOfDaysDiffs = [minDuration, maxDuration].map(
                    (duration) => {
                        const futureDate = DateUtils.addDays(from, duration);

                        return DateUtils.getDateDiff(futureDate, from);
                    }
                );

                const isInvalidRange = listOfDaysDiffs.some(
                    (daysDiff) => selectedDays < daysDiff
                );
                if (isInvalidRange) {
                    return {
                        error: durationLongerError,
                        offset: listOfDaysDiffs[listOfDaysDiffs.length - 1] - 1,
                    };
                }
            }
            return {
                error: CrossValidationError.NoError,
                offset: 0,
            };
        },
        []
    );

    const validateDuration = useCallback(
        (duration: DurationValue) => {
            if (!departureDate || !returnDate) {
                return CrossValidationError.NoError;
            }

            const validation = validate(
                departureDate,
                returnDate,
                duration,
                ValidationType.Duration
            );
            if (validation.offset > 0) {
                setReturnDate(
                    DateUtils.addDays(departureDate, validation.offset)
                );
                setHasValidationChange(true);
            }

            return validation.error;
        },
        [departureDate, returnDate, setReturnDate, validate]
    );

    const validateTravelPeriod = useCallback(
        (from?: Date, to?: Date) => {
            if (!from || !to || (from && to && DateUtils.isBefore(to, from))) {
                return CrossValidationError.NoError;
            }

            const validation = validate(
                from,
                to,
                durationValue,
                ValidationType.Datepicker
            );

            return validation.error;
        },
        [durationValue, validate]
    );

    useEffect(() => {
        if (departureDate && returnDate && hasValidationChange) {
            setError(
                validate(
                    departureDate,
                    returnDate,
                    durationValue,
                    ValidationType.Datepicker
                ).error
            );
            setHasValidationChange(false);
        }
    }, [
        departureDate,
        returnDate,
        durationValue,
        validate,
        hasValidationChange,
        setError,
    ]);

    return {
        validateDuration,
        validateTravelPeriod,
        openDurationOnEmpty: durationValue.type === DurationType.Empty,
    };
};
